// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Ticket card (transaction history list item)
// ---------------------------------------------------------------------------

import { formatPrice } from '@/lib/price-formatter';

interface TicketPayment {
  type: string;
  amount: number;
}

interface TicketItem {
  name: string;
  quantity: number;
  price: number;
}

export interface TicketData {
  id?: number | string;
  transaction_id?: string;
  timestamp?: string;
  client_created_at?: string;
  total?: number;
  total_amount?: number;
  items_count?: number;
  items?: TicketItem[];
  payments?: TicketPayment[];
  status?: string;
  is_cancellation?: boolean;
  synced?: boolean;
}

interface TicketCardProps {
  ticket: TicketData;
  onClick: () => void;
}

const PAYMENT_BADGE: Record<string, string> = {
  CB: 'bg-info/20 text-info',
  ESPECES: 'bg-success/20 text-success',
  PREVENTE: 'bg-warning/20 text-warning',
};

const STATUS_BADGE: Record<string, string> = {
  completed: 'bg-success/20 text-success',
  refunded: 'bg-danger/20 text-danger',
  cancelled: 'bg-gray-500/20 text-gray-400',
  pending: 'bg-warning/20 text-warning',
};

const STATUS_LABELS: Record<string, string> = {
  completed: 'Payee',
  refunded: 'Annulee',
  cancelled: 'Annulee',
  pending: 'En attente',
};

function formatTime(timestamp?: string): string {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function TicketCard({ ticket, onClick }: TicketCardProps) {
  const ts = ticket.timestamp || ticket.client_created_at;
  const total = ticket.total ?? ticket.total_amount ?? 0;
  const itemCount = ticket.items_count ?? ticket.items?.length ?? 0;
  const status = ticket.status ?? 'completed';
  const ticketId = ticket.id ?? ticket.transaction_id ?? 'N/A';
  const statusBadge = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
  const statusLabel = STATUS_LABELS[status] ?? status;

  return (
    <button
      type="button"
      onClick={onClick}
      className="card mx-4 mb-3 w-auto text-left animate-fade-in active:scale-[0.98] transition-transform"
    >
      {/* Header: ID + time */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-accent">#{String(ticketId).slice(-6)}</span>
          {ticket.is_cancellation && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-danger/20 text-danger">
              ANNULATION
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge}`}>
            {statusLabel}
          </span>
          <span className="text-xs text-gray-400">{formatTime(ts)}</span>
        </div>
      </div>

      {/* Body */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm text-gray-300">{itemCount} article{itemCount > 1 ? 's' : ''}</p>

          {/* Payment badges */}
          {(ticket.payments ?? []).length > 0 && (
            <div className="flex gap-1.5">
              {(ticket.payments ?? []).map((p, idx) => {
                const badgeClass = PAYMENT_BADGE[p.type?.toUpperCase()] ?? 'bg-surface-hover text-gray-400';
                return (
                  <span
                    key={idx}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${badgeClass}`}
                  >
                    {p.type}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Total */}
        <span className="text-lg font-bold text-success">
          {formatPrice(total)}
        </span>
      </div>

      {/* Sync status indicator */}
      {ticket.synced === false && (
        <div className="mt-2 flex items-center gap-1 text-[11px] text-warning">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          En attente de synchronisation
        </div>
      )}
    </button>
  );
}
